import { encodeRouteParam } from "@/utils/url-utils";
import { FeatureInterface, ResolverInterface } from "@/utils/graph/GraphInterface";
import { Routes } from "@/utils/routes";
import { environmentStaticPages } from "@/gen_src/environment_static_pages";
import type { NextRouter } from "next/router";
import { DynamicQueryParam } from "@/constants/query-params";

const environmentPrefixPattern = /^\/projects\/[^/?#]+\/environments\/[^/?#]+/;
const environmentRouteTemplate = "/projects/[projectId]/environments/[environmentId]";

const isDynamicRouteSegment = (segment: string) => segment.startsWith("[");

// The time selector applies to any environment, so it survives every switch.
const timeRangeQueryParams: readonly string[] = [
  DynamicQueryParam.LOOKBACK_MS,
  DynamicQueryParam.START_DATE,
  DynamicQueryParam.END_DATE,
];
const isTimeRangeQueryParam = (key: string) => timeRangeQueryParams.includes(key);
// A selected tab applies only to the same page, so it survives only an untruncated switch. Tabs
// are `v`, or `${namespace}.v` when written by useTypesafeQueryState with a namespace.
const isSamePageQueryParam = (key: string) =>
  isTimeRangeQueryParam(key) ||
  key === DynamicQueryParam.VIEW ||
  key.endsWith(`.${DynamicQueryParam.VIEW}`);

/** Keeps the allowed `key=value` pairs of a raw query string verbatim, so values are not re-encoded. */
const filterQueryString = (query: string, isAllowedKey: (key: string) => boolean): string => {
  const kept = query.split("&").filter((pair) => {
    const rawKey = pair.split("=", 1)[0].replaceAll("+", " ");
    try {
      return isAllowedKey(decodeURIComponent(rawKey));
    } catch {
      return false;
    }
  });
  return kept.length > 0 ? `?${kept.join("&")}` : "";
};

/**
 * Points the current page at another project and environment, keeping only the tab and time-range
 * query params. A URL with no IDs beyond the project and environment keeps its path and hash. A
 * URL carrying another ID (for example a resolver or connection) may not resolve in the target
 * environment, so it is truncated to its longest ID-free ancestor that is a page in every
 * environment, and keeps only the time range.
 */
export const replaceProjectAndEnv = (
  { pathname, asPath }: Pick<NextRouter, "pathname" | "asPath">,
  projectId: string,
  envId: string,
): string => {
  const prefix = environmentPrefixPattern.exec(asPath)?.[0];
  if (prefix == null) return asPath;

  const root = Routes.project.environment.root(projectId, envId);
  const [, path = "", query = "", hash = ""] =
    /^([^?#]*)(?:\?([^#]*))?(#.*)?$/.exec(asPath.slice(prefix.length)) ?? [];
  const urlSegments = path.split("/").filter(Boolean);
  // `pathname` is the page's route template, which aligns segment-for-segment with `asPath` because
  // no rewrites target environment pages. Outside an environment page (e.g. the 404 page) the
  // route is unknown, so every segment is treated as a potential ID.
  const routeSegments = pathname.startsWith(`${environmentRouteTemplate}/`)
    ? pathname.slice(environmentRouteTemplate.length + 1).split("/")
    : pathname === environmentRouteTemplate
      ? []
      : null;
  const firstIdIndex = routeSegments == null ? 0 : routeSegments.findIndex(isDynamicRouteSegment);
  // An optional catch-all with no URL segments carries no ID.
  if (firstIdIndex === -1 || urlSegments.length <= firstIdIndex) {
    return `${root}${path}${filterQueryString(query, isSamePageQueryParam)}${hash}`;
  }

  const timeRange = filterQueryString(query, isTimeRangeQueryParam);
  for (let length = firstIdIndex; length > 0; length--) {
    const ancestor = urlSegments.slice(0, length).join("/");
    if (environmentStaticPages.has(ancestor)) return `${root}/${ancestor}${timeRange}`;
  }
  return `${root}${timeRange}`;
};

export function getFeaturesNamespaceLinkForRoutingData({
  projectId,
  environmentId,
  namespace,
  branchData,
  nonActiveDeploymentId,
}: {
  projectId: string;
  environmentId: string;
  namespace: string;
  branchData?: { branchName: string; branchDeploymentId: string };
  nonActiveDeploymentId?: string;
}) {
  if (branchData != null) {
    return Routes.project.environment.branchDeploymentFeatures(
      projectId,
      environmentId,
      branchData.branchName,
      branchData.branchDeploymentId,
      namespace,
    );
  } else if (nonActiveDeploymentId != null) {
    return Routes.project.environment.deploymentFeatures(
      projectId,
      environmentId,
      nonActiveDeploymentId,
      namespace,
    );
  }

  return Routes.project.environment.features(projectId, environmentId, namespace);
}

// fix for backwards compatibility
const BAD_SOURCE_FILENAME_PREFIX = "/workspace/venv_source/project_root/";
export function normalizeSourceFilename(filename: string): string {
  if (filename.startsWith(BAD_SOURCE_FILENAME_PREFIX)) {
    return filename.replace(BAD_SOURCE_FILENAME_PREFIX, "");
  }
  return filename;
}

export function getResolverSourceLink({
  projectId,
  environmentId,
  branchData,
  deploymentId,
  resolver,
}: {
  projectId: string;
  environmentId: string;
  branchData?: { branchName: string; branchDeploymentId: string };
  deploymentId: string;
  resolver: ResolverInterface;
}) {
  // we mark resolvers with unknown filenames as having "<unknown file>"
  if (resolver.filename == null || resolver.filename.startsWith("<")) return undefined;

  return Routes.project.environment.sourceCodeTarget(
    projectId,
    environmentId,
    branchData != null ? branchData.branchDeploymentId : deploymentId,
    normalizeSourceFilename(resolver.filename ?? ""),
    resolver.sourceLine,
    resolver.functionDefinition?.split("\n").length,
  );
}

export interface FeatureSourceTarget {
  filename: string;
  line?: number;
  extent?: number;
}

/**
 * Where a feature is defined, in the shape the source-code viewer's route takes.
 * Features whose definition the graph could not attribute — an empty filename, or the
 * `<unknown file>` placeholder — have no target.
 */
export function getFeatureSourceTarget(feature: FeatureInterface): FeatureSourceTarget | undefined {
  const source = feature.sourceFileReference;
  if (source == null || source.fileName === "" || source.fileName.startsWith("<")) {
    return undefined;
  }

  const startLine = source.range?.start?.line;
  const endLine = source.range?.end?.line;

  return {
    filename: normalizeSourceFilename(source.fileName),
    line: startLine != null ? startLine + 1 : undefined,
    extent: startLine != null && endLine != null ? endLine - startLine : undefined,
  };
}

export function getFeatureSourceLink({
  projectId,
  environmentId,
  branchData,
  deploymentId,
  feature,
}: {
  projectId: string;
  environmentId: string;
  branchData?: { branchName: string; branchDeploymentId: string };
  deploymentId: string;
  feature: FeatureInterface;
}) {
  const target = getFeatureSourceTarget(feature);
  if (target == null) {
    return undefined;
  }

  return Routes.project.environment.sourceCodeTarget(
    projectId,
    environmentId,
    branchData?.branchDeploymentId ?? deploymentId,
    target.filename,
    target.line,
    target.extent,
  );
}

export function getDeploymentLink({
  deploymentId,
  branchData,
  projectId,
  environmentId,
}: {
  projectId: string;
  environmentId: string;
  deploymentId: string;
  branchData?: { branchName: string; branchDeploymentId: string };
}) {
  if (branchData != null) {
    return Routes.project.environment.branchDeployment(
      projectId,
      environmentId,
      branchData.branchName,
      branchData.branchDeploymentId,
    );
  }

  return Routes.project.environment.deployment(projectId, environmentId, deploymentId);
}

export const getEnvironmentRedirectHref = (
  projectId: string,
  environmentId: string,
  rest: string[],
  queryParams?: Record<string, string | string[] | undefined>,
): string => {
  const basePath =
    Routes.project.environment.root(projectId, environmentId) +
    (rest.length > 0 ? `/${rest.map(encodeRouteParam).join("/")}` : "");

  if (queryParams && Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (key !== "environmentId" && value != null) {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });
    const queryString = searchParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }

  return basePath;
};
